import type { AlgorithmOperation } from "@algoquest/schemas";
import { InsertionSortEngine, type InsertionSortDomainState } from "./insertion-sort";
import type { SortActionOutcome, SortController, SortDecision, SortViewState } from "./SortController";

/**
 * Phase 08 — wraps InsertionSortEngine behind the shared SortController
 * contract, replacing GameRuntime's old `tryInspectComparison` /
 * `tryDecideInsertion` / `applyInsertionSortStateToBoxes` /
 * `updateInsertionComparePointer` methods (their guard logic is
 * reproduced here verbatim, just returning a SortActionOutcome instead
 * of mutating GameRuntime state directly).
 *
 * toView() translates the engine's own domain state into SortViewState:
 * `requiredIndex`/`compareIndices` mirror the engine's own compareIndex/
 * keyIndex pair, in the same order the engine's own COMPARE operation
 * uses; `stickyIndex` is the carried key (`keyIndex`), which stays
 * highlighted across an entire round; `swaps` is always 0 (Master Spec
 * §40 tracks `shifts` as Insertion Sort's own distinct metric).
 *
 * Insertion Sort's domain state has no `sortedIndices` field the way
 * Bubble Sort's and Selection Sort's do (those two commit a final index
 * to the sorted region as a discrete engine event; Insertion Sort's
 * sorted region is instead an implicit boundary that grows every round),
 * so this controller computes it: when complete, every index is sorted;
 * otherwise every index up to and including `sortedBoundary` is sorted
 * EXCEPT `keyIndex` (the key's current resting slot, still being
 * carried) — this mirrors the exact formula the old
 * `applyInsertionSortStateToBoxes` used directly against GameRuntime's
 * SortBoxRow, and relies on the same invariant that method's doc comment
 * already established: `sortedBoundary < values.length` always holds
 * whenever `!isComplete`, per `concludeRound()`'s own logic, so no
 * additional clamping beyond the "all indices" special case is needed.
 */
function computeSortedIndices(state: InsertionSortDomainState): number[] {
  if (state.isComplete) {
    return state.values.map((_, index) => index);
  }
  const sortedIndices: number[] = [];
  for (let i = 0; i <= state.sortedBoundary; i += 1) {
    if (i !== state.keyIndex) {
      sortedIndices.push(i);
    }
  }
  return sortedIndices;
}

function toView(state: InsertionSortDomainState): SortViewState {
  return {
    algorithm: "insertion-sort",
    // The engine keeps the carried key outside its array during shifts.
    // Present that key in its current slot instead of showing a duplicated value.
    values: state.values.map((value, index) => !state.isComplete && index === state.keyIndex ? state.keyValue : value),
    sortDirection: state.sortDirection,
    requiredIndex: state.compareIndex,
    compareIndices: [state.compareIndex, state.keyIndex],
    stickyIndex: state.keyIndex,
    awaitingDecision: state.awaitingDecision,
    sortedIndices: computeSortedIndices(state),
    comparisons: state.comparisons,
    swaps: 0,
    shifts: state.shifts,
    passesCompleted: state.passesCompleted,
    mistakes: state.mistakes,
    isComplete: state.isComplete,
  };
}

export class InsertionSortController implements SortController {
  readonly algorithm = "insertion-sort" as const;
  private readonly engine: InsertionSortEngine;

  constructor(values: number[], sortDirection: "ascending" | "descending") {
    this.engine = new InsertionSortEngine(values, sortDirection);
  }

  getView(): SortViewState {
    return toView(this.engine.getState());
  }

  getTrace(): AlgorithmOperation[] {
    return this.engine.getTrace();
  }

  handlePrimaryAction(nearestIndex: number | null): SortActionOutcome {
    const state = this.engine.getState();
    if (state.isComplete || state.awaitingDecision || nearestIndex === null) {
      return { kind: "ignored" };
    }
    if (state.compareIndex !== nearestIndex) {
      return { kind: "rejected-wrong-box", expectedIndex: state.compareIndex };
    }
    const result = this.engine.inspect(performance.now());
    if (!result) {
      return { kind: "ignored" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }

  handleDecision(decision: SortDecision): SortActionOutcome {
    const before = this.engine.getState();
    if (!before.awaitingDecision) {
      return { kind: "ignored" };
    }
    const choice: "shift" | "insert" = decision === "advance" ? "shift" : "insert";
    const { wasCorrect, result } = this.engine.decide(choice, performance.now());
    if (!wasCorrect || !result) {
      return { kind: "rejected-wrong-decision" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }
}
